import { Link, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import type { FormEvent } from '../api/users';
import { useUserHistory } from '../hooks/useUsers';
import { usePageTitle } from '../hooks/usePageTitle';

interface HistoryRow {
  timestamp: number;
  actor: string;
  field: string;
  before: string | null;
  after: string | null;
}

function buildRows(events: FormEvent[]): HistoryRow[] {
  const sorted = [...events].sort((a, b) => a.timestamp - b.timestamp);
  const lastValue = new Map<string, string | null>();
  const rows: HistoryRow[] = [];
  for (const e of sorted) {
    const before = lastValue.has(e.field) ? (lastValue.get(e.field) ?? null) : null;
    rows.push({ timestamp: e.timestamp, actor: e.user, field: e.field, before, after: e.value });
    lastValue.set(e.field, e.value);
  }
  return rows.reverse();
}

function fmtValue(v: string | null): string {
  if (v === null) return '∅';
  if (v === '') return '(empty)';
  return v;
}

function fmtTimestamp(ms: number): string {
  return new Date(ms).toISOString().replace('T', ' ').replace(/\.\d+Z$/, ' UTC');
}

export default function UserHistoryPage() {
  const { email = '' } = useParams<{ email: string }>();
  usePageTitle(`${email} history`);
  const historyQ = useUserHistory(email, email.length > 0);

  if (historyQ.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (historyQ.error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" data-testid="admin-user-history-error">
          Error: {historyQ.error.message}
        </Alert>
      </Box>
    );
  }

  const rows = buildRows(historyQ.data ?? []);

  return (
    <Box className="admin-users" sx={{ p: 2 }} data-testid="admin-user-history-page">
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5">
            History: <span style={{ fontWeight: 400 }}>{email}</span>
          </Typography>
          <Button
            component={Link}
            to="/users"
            startIcon={<ArrowBackIcon />}
            size="small"
            data-testid="admin-user-history-back-button"
          >
            Back to users
          </Button>
        </Box>

        {rows.length === 0 ? (
          <Typography color="text.secondary" data-testid="admin-user-history-empty">
            No history recorded.
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" data-testid="admin-user-history-table">
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Actor</TableCell>
                  <TableCell>Field</TableCell>
                  <TableCell>Before</TableCell>
                  <TableCell>After</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={`${r.timestamp}-${r.field}-${i}`} data-testid={`admin-user-history-row-${i}`}>
                    <TableCell>{fmtTimestamp(r.timestamp)}</TableCell>
                    <TableCell>{r.actor}</TableCell>
                    <TableCell data-testid={`admin-user-history-field-${i}`}>{r.field}</TableCell>
                    <TableCell>{fmtValue(r.before)}</TableCell>
                    <TableCell>{fmtValue(r.after)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}
